import { useContext, useEffect, useState } from 'react'
import { Wrapper } from 'components'
import { AuthContext } from 'contexts/AuthContext'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Card,
  Drawer,
  DrawerContent,
  Input,
  Label,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Textarea,
  toast,
  Upload,
  UploadBody,
  UploadIcon,
  UploadText,
} from 'keep-react'
import { Rocket } from 'components/Icons/rocket'
import {
  ArrowLeft,
  CheckCircle,
  Download,
  Envelope,
  UploadSimple,
  WarningCircle,
} from 'phosphor-react'
import { downloadPDF } from 'utils/downloadPdf'
import { policiesService } from 'api/services/policies'
import { PolicyListResponse } from 'api/types/policies'
import { docService } from 'api/services/doc'
import {
  GetSimDocsResponseBody,
  SubmitDocumentResponseBody,
  VerifySignatureResponseBody,
} from 'api/types/doc'
import { simService } from 'api/services/sim'
import { RouteNamesAuth } from 'routes/enum'
import { formatCurrency } from 'utils/currency'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { emailService } from 'api/services/email'
import { renderBadge } from 'utils/renderBadge'
import { AppContext } from 'contexts/AppContext'
import { ProductType } from 'api/types/product'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Obrigatório'),
  email: Yup.string().email('Email inválido').required('Obrigatório'),
  CCEmail: Yup.string().email('Email inválido'),
  BCCEmail: Yup.string().email('Email inválido'),
})

export const Quotation = () => {
  const { policyNo, eventNo } = useParams() as {
    policyNo: string
    eventNo: string
  }
  const navigate = useNavigate()
  const [quotation, setQuotation] = useState<any>(null)
  const { currentUser, currentBroker } = useContext(AuthContext)
  const { setIsCallingApi, products } = useContext(AppContext)
  const brokerNo = currentBroker?.BrokerNo ? currentBroker?.BrokerNo : 0
  const contactNo = currentUser?.ContactNo || 0
  const [showDrawer, setShowDrawer] = useState<boolean>(false)
  const [showFileUpload, setShowFileUpload] = useState<boolean>(false)
  const [fullProcess, setFullProcess] = useState<0 | 1>(0)
  const [createBusiness, setCreateBusiness] = useState<boolean>(false)
  const [sendEmailModal, setSendEmailModal] = useState<boolean>(false)
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false)
  const [simulatorExists, setSimulatorExists] = useState<boolean>(false)

  useEffect(() => {
    const fetchQuotation = async () => {
      if (!policyNo || contactNo === 0 || brokerNo === 0) return false

      try {
        const response = (await policiesService.getQuotationList({
          policyNo: Number(policyNo),
          eventNo: Number(eventNo),
          brokerNo,
          page: 0,
        })) as PolicyListResponse

        setQuotation(response.Policies[0])
      } catch (error) {
        console.error('Error fetching quotation:', error)
      }
    }
    fetchQuotation()
  }, [policyNo, contactNo, brokerNo])

  useEffect(() => {
    if (!products || products.length === 0 || !quotation) return

    const productFromList = products.find(
      p => p.product === quotation.CodigoProduto,
    ) as ProductType | undefined

    if (productFromList) {
      setSimulatorExists(productFromList.hasSimulation)
    }
  }, [products, quotation])

  const getSimDocsHandler = async (index: number) => {
    if (!quotation || !policyNo) return
    setIsCallingApi(true)
    try {
      const response = (await docService.getSimDocs(
        Number(policyNo),
        quotation['LastEvent'],
      )) as GetSimDocsResponseBody

      downloadPDF({
        base64Data: response.Documents[index].Document,
        filename: `${index === 0 ? 'Cotação' : 'Proposta'} - ${policyNo}.pdf`,
      })
    } catch (error) {
      console.error('Error fetching document:', error)
    } finally {
      setIsCallingApi(false)
    }
  }

  const onFileUpload = (files: File[]) => {
    if (!files || !policyNo) return

    const reader = new FileReader()
    reader.onload = async () => {
      const base64 = reader.result
      if (base64 && typeof base64 === 'string') {
        try {
          const submitDocumentResponse = (await docService.submitDocument({
            policyNo: Number(policyNo)!,
            base64String: base64.replace(/^data:application\/pdf;base64,/, ''),
          })) as SubmitDocumentResponseBody

          if (submitDocumentResponse.Status.OK) {
            toast.success('Documento submetido com sucesso')

            setShowFileUpload(false)
            setIsCallingApi(true)
            try {
              const verifySignatureResponse = (await docService.verifySignature(
                {
                  policyNo: Number(policyNo)!,
                  base64String: base64.replace(
                    /^data:application\/pdf;base64,/,
                    '',
                  ),
                },
              )) as VerifySignatureResponseBody

              if (verifySignatureResponse.status) {
                setFullProcess(1)
                try {
                  const logInfo = await simService.logSimulationInfo({
                    simCode: '-',
                    eventType: 'PORTAL',
                    observation: String(policyNo!),
                  })

                  await simService.createNewBusiness({
                    policyNo: Number(policyNo!),
                    fullProcess: 1,
                    brokerNo: String(currentBroker?.BrokerNo),
                  })

                  setShowFileUpload(false)
                  setCreateBusiness(true)
                } catch (e) {
                  toast.error(
                    'Erro a emitir a apólice. Contacte a Innovarisk.',
                    {
                      duration: 12000,
                    },
                  )
                  setIsCallingApi
                }
              } else {
                setFullProcess(0)
                toast.error(
                  'Assinatura não validada. Não se preocupe! A sua assinatura será agora validada pela equipa Innovarisk.',
                  {
                    duration: 12000,
                  },
                )
              }
            } catch (e) {
              toast.error('Erro na verificação da assinatura.', {
                duration: 12000,
              })
            } finally {
              setIsCallingApi(false)
            }
          } else {
            toast.error('Erro ao submeter o documento.', {
              duration: 12000,
            })
          }
        } catch (error) {
          toast.error('Erro ao submeter o documento.', {
            duration: 12000,
          })
        } finally {
          setIsCallingApi(false)
        }
      }
    }
    reader.onerror = error => {
      console.error('Error reading file:', error)
    }

    if (files[0].type !== 'application/pdf') {
      toast.error('Por favor faça upload de um ficheiro PDF.')
      return
    }
    reader.readAsDataURL(files[0])
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      CCEmail: '',
      BCCEmail: '',
      EmailBody: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      setIsCallingApi(true)
      setIsSendingEmail(true)
      const body = {
        email: values.email,
        CCEmail: values.CCEmail,
        BCCEmail: values.BCCEmail,
        EmailBody: values.EmailBody,
        username: values.name,
        policyNo: policyNo,
        broker: currentBroker?.BrokerName || '',
        eventNo: quotation['LastEvent'] || '',
        product: quotation['Produto'] || '',
      }

      try {
        await emailService.sendProposalEmail(body)
        toast.success('Email enviado com sucesso.')
        setSendEmailModal(false)
      } catch (error) {
        toast.error('Erro ao enviar o email.')
        console.error('Error sending email:', error)
      } finally {
        setIsSendingEmail(false)
        setIsCallingApi(false)
      }
    },
  })

  if (!quotation) return <Spinner />

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <Button
          variant="outline"
          color="secondary"
          className="mb-6"
          onClick={() => navigate(RouteNamesAuth.QUOTATIONS)}
        >
          <ArrowLeft size={16} /> Cotações
        </Button>
        <h2 className="uppercase text-sm font-medium text-metal-900">
          Consulte a cotação gravada
        </h2>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600">
          Cotação Nº {policyNo}
        </h1>

        <div className="grid grid-col-1 lg:grid-cols-[3fr_1fr] gap-4 w-full">
          <Card className="p-4 w-full max-w-full">
            <div className="flex items-start gap-2">
              <div className="flex items-center flex-col gap-1 pt-3 -translate-y-1.5">
                <Rocket />
              </div>
              <h2 className="text-body-1 uppercase pb-10">
                {(quotation && quotation['Produto']) || 'Produto indisponível'}
              </h2>
            </div>

            <div>
              <Table className="pb-10">
                <TableBody>
                  {quotation &&
                    Object.entries(quotation).map(
                      (item: any, index: number) => {
                        const rejectsKeys = [
                          'LastEvent',
                          'AddressLine1',
                          'AddressLine2',
                          'AddressLine3',
                          'AddressLine4',
                          'Postcode',
                          'Country',
                          'Data da Renovação',
                          'CodigoProduto',
                          'Razão',
                          'ModifiedDate',
                        ]
                        if (rejectsKeys.includes(item[0])) return null
                        return (
                          <TableRow key={`row-${index}`}>
                            <TableHead>
                              {item[0] === 'ModifiedDate'
                                ? 'Data Modificação'
                                : item[0] === 'Branch'
                                  ? 'Mediador'
                                  : item[0] === 'Nº da Apólice'
                                    ? 'Nº da Cotação'
                                    : item[0]}
                            </TableHead>
                            <TableCell>
                              {item[0] === 'Capital Seguro' ||
                              item[0] === 'Prémio Anual'
                                ? formatCurrency(item[1])
                                : item[0] === 'Estado'
                                  ? renderBadge(item[1])
                                  : item[1]}
                            </TableCell>
                          </TableRow>
                        )
                      },
                    )}
                </TableBody>
              </Table>
            </div>
          </Card>
          <div>
            <Card className="p-4 w-full max-w-full">
              <nav className="flex flex-col items-start justify-start gap-4">
                <Button
                  color="secondary"
                  variant="softBg"
                  className="w-full gap-2 justify-start"
                  onClick={() => getSimDocsHandler(0)}
                >
                  <Download size={16} />
                  Download da Cotação
                </Button>

                <Button
                  color="secondary"
                  variant="softBg"
                  className="w-full gap-2 justify-start"
                  onClick={() => getSimDocsHandler(1)}
                >
                  <Download size={16} />
                  Download da Proposta
                </Button>

                <Button
                  color="secondary"
                  variant="outline"
                  className="w-full gap-2 justify-start"
                  onClick={() => setSendEmailModal(true)}
                >
                  <Envelope size={16} />
                  Enviar proposta por email
                </Button>

                {simulatorExists && (
                  <Button
                    color="secondary"
                    variant="outline"
                    className="w-full gap-2 justify-start"
                    onClick={() =>
                      navigate(
                        RouteNamesAuth.SIMULATOR.replace(
                          ':slug',
                          String(quotation['CodigoProduto']),
                        ),
                      )
                    }
                  >
                    <Envelope size={16} />
                    Simular novamente
                  </Button>
                )}

                {quotation['Estado'] === 'QUOTED' && (
                  <Button
                    color="primary"
                    variant="default"
                    className="w-full gap-2 justify-start"
                    onClick={() => setShowDrawer(true)}
                  >
                    <Download size={16} />
                    Emitir Apólice
                  </Button>
                )}
              </nav>
            </Card>
          </div>
        </div>
      </Wrapper>

      <Drawer
        open={showDrawer}
        showCloseIcon={false}
        onOpenChange={() => setShowDrawer(false)}
      >
        <DrawerContent position="right" className="bg-metal-25 xl:w-1/3">
          <div>
            <div className="pt-20">
              <h3 className="text-body-1 font-light uppercase tracking-wider">
                Contratação automática disponível
              </h3>
              <p className="text-body-4 text-metal-600">
                Para converter esta simulação em apólice, clicar em “Emitir
                apólice” ou gravar e aceder mais tarde através do ecrã
                “Cotações”.
              </p>

              <div className="pt-10">
                <Button
                  className="w-full"
                  color="primary"
                  type="button"
                  onClick={() => {
                    setShowFileUpload(true)
                  }}
                >
                  Emitir Apólice
                </Button>
              </div>
              {showFileUpload && policyNo && (
                <div className="py-4">
                  <Upload
                    horizontal
                    options={{ onDrop: onFileUpload, multiple: false }}
                  >
                    <UploadBody>
                      <UploadIcon>
                        <UploadSimple size={48} />
                      </UploadIcon>
                      <UploadText>
                        <p className="text-body-3 font-medium text-metal-600">
                          Por favor faça upload da proposta de seguro assinada
                          pelo Tomador para concluir a emissão da apólice.
                        </p>
                        <p className="text-body-4 font-normal text-metal-400">
                          Apenas 1 ficheiro PDF
                        </p>
                      </UploadText>
                    </UploadBody>
                  </Upload>
                </div>
              )}

              {createBusiness && (
                <div className="space-y-4">
                  <h3 className="text-body-1 text-primary-500 uppercase ">
                    Emissão
                  </h3>
                  <div className="p-4 bg-metal-800 rounded-lg border border-metal-100 grid grid-cols-[24px_1fr] gap-4">
                    {fullProcess === 1 && (
                      <CheckCircle size={24} className="text-success-500" />
                    )}
                    {fullProcess === 0 && (
                      <WarningCircle size={24} className="text-warning-500" />
                    )}
                    <div>
                      <p className="text-body-3 text-white font-bold pb-2">
                        {fullProcess === 1
                          ? 'Assinatura validada com sucesso'
                          : 'Assinatura não validada'}
                      </p>
                      <p className="text-body-3 text-white">
                        {fullProcess === 1
                          ? 'Obrigado por contratar com a Innovarisk. Receberá os dados para pagamento do seu seguro no seu email em 24 horas.'
                          : 'Não se preocupe. A sua assinatura será agora validada pela equipa Innovarisk.'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    {fullProcess === 1 && (
                      <Button
                        color="primary"
                        variant="softBg"
                        onClick={() => {
                          navigate(RouteNamesAuth.POLICIES)
                        }}
                      >
                        Ver Apólices
                      </Button>
                    )}
                    {fullProcess === 0 && (
                      <Button
                        color="primary"
                        variant="softBg"
                        onClick={() => {
                          navigate(RouteNamesAuth.QUOTATIONS)
                        }}
                      >
                        Ver Cotações
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/*  Modal to send quotation by email */}
      <Modal
        open={sendEmailModal}
        onOpenChange={() => setSendEmailModal(false)}
      >
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Enviar Cotação por Email</ModalTitle>
            <ModalDescription className="hidden">
              Modal para enviar proposta
            </ModalDescription>
            <form onSubmit={formik.handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  type="text"
                  name="name"
                  placeholder="nome"
                  onChange={formik.handleChange}
                  className={formik.errors.name ? 'border-error-500' : ''}
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="email"
                  onChange={formik.handleChange}
                  className={formik.errors.email ? 'border-error-500' : ''}
                />
              </div>
              <div>
                <Label htmlFor="CCEmail">CC Email</Label>
                <Input
                  id="CCEmail"
                  type="email"
                  name="CCEmail"
                  placeholder="CC email"
                  onChange={formik.handleChange}
                  className={formik.errors.CCEmail ? 'border-error-500' : ''}
                />
              </div>
              <div>
                <Label htmlFor="BCCEmail">BCC Email</Label>
                <Input
                  id="CCEmail"
                  type="email"
                  name="BCCEmail"
                  placeholder="BCC email"
                  onChange={formik.handleChange}
                  className={formik.errors.BCCEmail ? 'border-error-500' : ''}
                />
              </div>
              <div>
                <Label htmlFor="EmailBody">Corpo do Email</Label>
                <Textarea
                  id="EmailBody"
                  name="EmailBody"
                  placeholder="Corpo do email"
                  onChange={formik.handleChange}
                  className={formik.errors.EmailBody ? 'border-error-500' : ''}
                />
              </div>
            </form>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setSendEmailModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={() => formik.handleSubmit()}
              disabled={isSendingEmail}
            >
              {isSendingEmail ? <Spinner /> : 'Enviar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
